/**
 * Уведомления в браузере: что показывать и что промолчать.
 *
 * Ценность уведомления обратно пропорциональна их числу. Диспетчер, получивший
 * четыре одинаковых сообщения подряд, на пятое смотреть перестанет — и
 * пропустит настоящее. Поэтому здесь два фильтра.
 *
 * Первый — список того, о чём вообще стоит сообщать. Зажигание в симуляторе
 * щёлкает двадцать шесть раз за десять минут; такому место в журнале, а не во
 * всплывающем окне.
 *
 * Второй — гашение дребезга. Машина, идущая по касательной к границе зоны,
 * даёт въезд и выезд каждые десять секунд: точки приходят редко, и соседние
 * оказываются то внутри круга, то снаружи. Это одно пересечение, а не четыре
 * события; проверено на симуляторе. Своего гашения у Traccar нет.
 */

import type { EventRow } from './api'

/** Окно гашения. Внутри него повтор считается тем же самым происшествием. */
export const WINDOW_MS = 60_000

/** Сколько записей держать в памяти, чтобы она не росла всю смену. */
const MAX_REMEMBERED = 500

/**
 * События, о которых стоит сообщать.
 *
 * Набор выбран по одному правилу: сообщаем о том, на что человек должен
 * ответить действием. Заезд в зону и выезд из неё, пропавшее питание, тревога
 * — всё это требует решения. Начало движения и щелчок зажигания не требуют
 * ничего.
 *
 * `deviceOffline` убран (решение владельца 28.09): FMC150 замолкает на каждой
 * стоянке по замыслу, и уведомление «пропала со связи» пугало клиента
 * несколько раз в день, не требуя никакого действия.
 */
export const WORTH_TELLING = new Set([
  'geofenceEnter',
  'geofenceExit',
  'powerCut',
  'alarm',
  'commandResult',
  'queuedCommandSent',
  'unaccompaniedMotion',
])

/**
 * Ключ происшествия.
 *
 * У зоны въезд и выезд делят один ключ намеренно: дребезг на границе — это
 * колебания вокруг одного пересечения, и гасить их надо вместе.
 */
function key(event: EventRow): string {
  if (event.type.startsWith('geofence')) {
    return `zone:${event.deviceId}:${event.geofenceId ?? '?'}`
  }
  return `${event.deviceId}:${event.type}`
}

/** Решает, показывать ли уведомление, и помнит недавно показанные. */
export class Gate {
  private readonly shown = new Map<string, number>()

  // Поле объявлено отдельно, а не сокращением в списке параметров: Node
  // запускает тесты, просто срезая типы, а сокращение — не тип, а код.
  private readonly windowMs: number

  constructor(windowMs: number = WINDOW_MS) {
    this.windowMs = windowMs
  }

  get size(): number {
    return this.shown.size
  }

  admit(event: EventRow, now: number = Date.now()): boolean {
    if (!WORTH_TELLING.has(event.type)) return false

    const id = key(event)
    const last = this.shown.get(id)
    if (last !== undefined && now - last <= this.windowMs) return false

    this.shown.set(id, now)
    this.forget(now)
    return true
  }

  /** Убирает записи старше окна; иначе за смену их накопятся десятки тысяч. */
  private forget(now: number): void {
    if (this.shown.size <= MAX_REMEMBERED) return
    for (const [id, when] of this.shown) {
      if (now - when > this.windowMs) this.shown.delete(id)
    }
  }
}

/** Разрешены ли уведомления браузером. */
export function notificationsAllowed(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted'
}

/** Спрашивает разрешение. Браузеры требуют, чтобы это шло из действия человека. */
export async function askForNotifications(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  return (await Notification.requestPermission()) === 'granted'
}

/** Показывает уведомление. Тихо ничего не делает, если их не разрешили. */
export function show(title: string, body: string): void {
  if (!notificationsAllowed()) return
  try {
    new Notification(title, { body, tag: title + body })
  } catch {
    // На некоторых браузерах уведомления доступны только через service worker.
    // Это не повод ронять экран: событие всё равно видно в журнале.
  }
}
